import './cutting-edit-actions.css';

import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';
import { cloneDeep, isEmpty } from 'lodash';
import { ReactElement, ReactNode, useCallback, useMemo, useRef } from 'react';
import { MdCompress, MdExpand } from 'react-icons/md';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import {
  FlowActionBarEditAction,
  FlowActionBarEditActionsProps,
} from '../../../../shared/component/action/flow-action-bar-edit-actions.types';
import { FlowItemIcon } from '../../../../shared/component/flow-item/view/flow-item-icon';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  Flow,
  FlowItem,
  FlowItemSharedParameterRef,
  FlowSelectedItemStats,
} from '../../../../shared/model/flow.types';
import { getParameterValue } from '../../../../shared/util/flow-parameter.util';
import { scrollToFlowItem } from '../../../../shared/util/flow.util';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';
import { isCutType } from '../../util/beacon-cutting.util';
import {
  getFlowItemIcon,
  getWaitForType,
} from '../flow-item/cutting-flow-item';

const CuttingEditActions = (
  props: FlowActionBarEditActionsProps
): ReactElement => {
  const { flow, flowRef, dismiss } = props;

  const { selectedItemDetails, setSelectedItemIds, toggleCollapseAll } =
    flowRef;

  const { flowAnalyticService } = useFlowServices();

  const { selectedItemIds, selectedFlowItemInfo: selectedItemInfo } =
    selectedItemDetails;

  const toggleCutType = useRef<
    (cutType: BeaconCuttingData.CutTypeEnum) => void
  >(() => null);

  const getOtherIconsRef = useRef<() => ReactNode[]>(() => []);
  const isOtherTypeSelected = isTypeSelected(selectedItemInfo?.other);

  getOtherIconsRef.current = () =>
    getFirstNOtherTypes(flow, 2, isOtherTypeSelected).map((el, index) => {
      return (
        <div
          key={index}
          style={{
            position: index === 0 ? 'unset' : 'absolute',
            transform: `translateX(${8 * index}px)`,
            zIndex: -index,
          }}
        >
          {el}
        </div>
      );
    });

  toggleCutType.current = (cutType: BeaconCuttingData.CutTypeEnum): void => {
    let stats: FlowSelectedItemStats | undefined;
    let type = 'other';
    switch (cutType) {
      case BeaconCuttingData.CutTypeEnum.LINEAR:
        stats = selectedItemInfo?.linear;
        type = 'linear';
        break;
      case BeaconCuttingData.CutTypeEnum.RADIAL:
        stats = selectedItemInfo?.radial;
        type = 'radial';
        break;
      case BeaconCuttingData.CutTypeEnum.ROTARY:
        stats = selectedItemInfo?.rotary;
        type = 'rotary';
        break;
    }

    if (stats && stats.count === stats.selected) {
      const nextSelectedItemIds = unselectCutTypes(
        flow,
        selectedItemIds,
        cutType
      );
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(unselectCutTypes(flow, selectedItemIds, cutType));
      flowAnalyticService.trackUnselectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: prevSelected - totalSelected,
        totalSelected,
        type,
      });
    } else {
      const nextSelectedItemIds = selectCutTypes(
        flow,
        selectedItemIds,
        cutType
      );
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(nextSelectedItemIds);
      flowAnalyticService.trackSelectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: totalSelected - prevSelected,
        totalSelected,
        type,
      });
    }
  };

  const toggleOtherTypes = useRef<() => void>(() => null);

  toggleOtherTypes.current = (): void => {
    if (
      selectedItemInfo &&
      selectedItemInfo.other.count === selectedItemInfo.other.selected
    ) {
      const nextSelectedItemIds = unselectOtherTypes(flow, selectedItemIds);
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(nextSelectedItemIds);
      flowAnalyticService.trackUnselectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: prevSelected - totalSelected,
        totalSelected,
        type: 'other',
      });
    } else {
      const nextSelectedItemIds = selectOtherTypes(flow, selectedItemIds);
      const prevSelected = selectedItemIds.length;
      const totalSelected = nextSelectedItemIds.length;
      setSelectedItemIds(nextSelectedItemIds);
      flowAnalyticService.trackSelectItems({
        method: 'actionBar',
        flowLength: flow.items.length,
        count: totalSelected - prevSelected,
        totalSelected,
        type: 'other',
      });
    }
  };

  const sections = getSections(flow);
  const hasSections = useMemo(
    () => sections.find((section) => section.id !== undefined) !== undefined,
    [sections]
  );

  const getActions: () => FlowActionBarEditAction[] = useCallback(() => {
    return [
      {
        label: `${getLocalizedText('cutting:select_linear')} (${
          selectedItemInfo?.linear.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconCuttingData.FlowItemRef.CUT,
              BeaconCuttingData.CutTypeEnum.LINEAR
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.linear)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.linear),
        onSelect: () => {
          toggleCutType.current(BeaconCuttingData.CutTypeEnum.LINEAR);
        },
      },
      {
        label: `${getLocalizedText('cutting:select_radial')} (${
          selectedItemInfo?.radial.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconCuttingData.FlowItemRef.CUT,
              BeaconCuttingData.CutTypeEnum.RADIAL
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.radial)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.radial),
        onSelect: () => {
          toggleCutType.current(BeaconCuttingData.CutTypeEnum.RADIAL);
        },
      },
      {
        label: `${getLocalizedText('cutting:select_rotary')} (${
          selectedItemInfo?.rotary.count ?? 0
        })`,
        icon: (
          <FlowItemIcon
            className="edit-action-icon"
            icon={getFlowItemIcon(
              BeaconCuttingData.FlowItemRef.CUT,
              BeaconCuttingData.CutTypeEnum.ROTARY
            )}
            isItemSelected={isTypeSelected(selectedItemInfo?.rotary)}
          />
        ),
        disabled: isTypeDisabled(selectedItemInfo?.rotary),
        onSelect: () => {
          toggleCutType.current(BeaconCuttingData.CutTypeEnum.ROTARY);
        },
      },
      {
        label: `${getLocalizedText('cutting:select_other')} (${
          selectedItemInfo?.other.count ?? 0
        })`,
        disabled: isTypeDisabled(selectedItemInfo?.other),
        icon: (
          <div style={{ display: 'flex' }}>{getOtherIconsRef.current()}</div>
        ),
        onSelect: () => {
          toggleOtherTypes.current();
        },
      },
    ];
  }, [selectedItemInfo]);

  const actions = getActions();

  return (
    <div className="edit-actions-container">
      <div className="edit-actions-button-container">
        <IonButton
          slot="start"
          fill="clear"
          onClick={() => {
            setSelectedItemIds(selectAll(flow));
            flowAnalyticService.trackSelectAllItems(flow.items.length);
            if (dismiss) {
              dismiss();
            }
          }}
        >
          <IonLabel color="primary">
            {getLocalizedText('common:select_all')}
          </IonLabel>
        </IonButton>
        <IonButton
          slot="end"
          fill="clear"
          onClick={() => {
            setSelectedItemIds(unselectAll());
            flowAnalyticService.trackUnselectAllItems(flow.items.length);
            if (dismiss) {
              dismiss();
            }
          }}
        >
          <IonLabel color="primary">
            {getLocalizedText('common:unselect_all')}
          </IonLabel>
        </IonButton>
      </div>
      <IonList>
        <IonListHeader>
          {getLocalizedText('cutting:customize_select')}
        </IonListHeader>
        {actions.map((a, index) => {
          const lines = index === actions.length - 1 ? 'none' : 'full';
          return (
            <IonItem
              className={a.disabled ? 'flow-item-inactive' : ''}
              lines={lines}
              key={index}
              href="#"
              detail={false}
              disabled={a.disabled}
              onClick={(e) => {
                e.preventDefault();
                a.onSelect();
              }}
            >
              {a.icon ?? (
                <IonIcon
                  slot="start"
                  size="small"
                  icon={checkmark}
                  color={a.isSelected ? 'primary' : 'light'}
                />
              )}
              <IonLabel>{a.label}</IonLabel>
            </IonItem>
          );
        })}
        <SectionActions
          hasSections={hasSections}
          sections={sections}
          toggleCollapseAll={toggleCollapseAll}
          dismiss={dismiss}
        />
      </IonList>
      {hasSections && <div className="popover-fade" />}
    </div>
  );
};

interface SectionActionProps {
  hasSections: boolean;
  sections: FlowItem[] | undefined;
  toggleCollapseAll: (collapsed: boolean) => void;
  dismiss?: () => void;
}

const SectionActions = (props: SectionActionProps): ReactElement | null => {
  const { hasSections, sections, toggleCollapseAll, dismiss } = props;

  const { flowItemService, flowAnalyticService } = useFlowServices();
  const sectionTexts = flowItemService.buildFlowItemTextArr(sections ?? []);

  const jumpToSectionActions: ReactElement[] =
    sections?.reduce((actions, section, idx) => {
      if (section.id === undefined) {
        return actions;
      }

      const color = getParameterValue(
        section,
        FlowItemSharedParameterRef.SECTION_COLOR
      );
      const borderLeftColor = color ? `${color}` : `transparent`;

      const sectionLabel =
        sectionTexts[idx]?.label ??
        sectionTexts[idx]?.name ??
        getLocalizedText('common:section');

      const lines = idx === sections.length - 1 ? 'none' : 'full';
      actions.push(
        <IonItem
          lines={lines}
          key={idx}
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            if (section.id !== undefined) {
              scrollToFlowItem(section.id, { skipHeaderPadding: true });
              flowAnalyticService.trackSectionJumpTo();
              if (dismiss) {
                dismiss();
              }
            }
          }}
        >
          <div
            // slot="start"
            className="edit-action-section-indicator"
            style={{
              borderLeftColor,
            }}
          />
          <IonLabel>{sectionLabel}</IonLabel>
        </IonItem>
      );
      return actions;
    }, [] as ReactElement[]) ?? [];

  if (hasSections && sections) {
    return (
      <>
        <div style={{ height: '20px' }} />
        <IonListHeader>
          {getLocalizedText('common:section_shortcuts')}
        </IonListHeader>
        <IonItem
          className={'flow-item-inactive'}
          lines={'full'}
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            toggleCollapseAll(false);
            flowAnalyticService.trackSectionExpandAll();
          }}
        >
          <MdExpand />
          <IonLabel>{getLocalizedText('common:section_expand_all')}</IonLabel>
        </IonItem>
        <IonItem
          className={'flow-item-inactive'}
          lines={'none'}
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            toggleCollapseAll(true);
            flowAnalyticService.trackSectionCollapseAll();
          }}
        >
          <MdCompress />
          <IonLabel>{getLocalizedText('common:section_collapse_all')}</IonLabel>
        </IonItem>
        <div style={{ height: '10px' }} />
        <IonListHeader>
          {getLocalizedText('common:section_jump_to')}
        </IonListHeader>
        {jumpToSectionActions}
        <div style={{ height: '10px' }} />
      </>
    );
  } else {
    return null;
  }
};

const isTypeSelected = (stats?: FlowSelectedItemStats): boolean => {
  if (stats && stats.count == stats.selected && stats.count > 0) {
    return true;
  }
  return false;
};

const isTypeDisabled = (stats?: FlowSelectedItemStats): boolean => {
  if (stats && stats.count === 0) {
    return true;
  }
  return false;
};

const selectCutTypes = (
  flow: Flow,
  selectedItemIds: string[],
  cutType: BeaconCuttingData.CutTypeEnum
): string[] => {
  const selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (
      isCutType(item, cutType) &&
      item.id &&
      !selectedItemIds.includes(item.id)
    ) {
      selected.push(item.id);
    }
  });
  return selected;
};

const unselectCutTypes = (
  flow: Flow,
  selectedItemIds: string[],
  cutType: BeaconCuttingData.CutTypeEnum
): string[] => {
  let selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (isCutType(item, cutType)) {
      selected = selected.filter((i) => i !== item.id);
    }
  });
  return selected;
};

const selectOtherTypes = (flow: Flow, selectedItemIds: string[]): string[] => {
  const selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (
      item.ref !== BeaconCuttingData.FlowItemRef.CUT &&
      item.id &&
      !selectedItemIds.includes(item.id)
    ) {
      selected.push(item.id);
    }
  });
  return selected;
};

const unselectOtherTypes = (
  flow: Flow,
  selectedItemIds: string[]
): string[] => {
  let selected = cloneDeep(selectedItemIds);
  flow.items.map((item) => {
    if (
      item.ref !== BeaconCuttingData.FlowItemRef.CUT &&
      item.id &&
      selectedItemIds.includes(item.id)
    ) {
      selected = selected.filter((i) => i !== item.id);
    }
  });
  return selected;
};

const getSections = (flow: Flow, saved?: boolean) => {
  return flow.items.filter((item) => {
    return (
      item.ref === BeaconCuttingData.FlowItemRef.SECTION &&
      (saved ? item.id !== undefined : true)
    );
  });
};

const selectAll = (flow: Flow): string[] => {
  return flow.items.map((item) => item.id as string);
};

const unselectAll = (): string[] => {
  return [];
};

const getFirstNOtherTypes = (
  flow: Flow,
  num = 3,
  isSelected?: boolean
): ReactNode[] => {
  const icons: ReactNode[] = [];
  const refsCollected: string[] = [];

  const defaultIcon = (
    <FlowItemIcon
      className="edit-action-icon"
      icon={getFlowItemIcon(BeaconCuttingData.FlowItemRef.MOVE)}
      isItemSelected={isSelected}
    />
  );

  flow.items.map((item) => {
    if (
      item.ref !== BeaconCuttingData.FlowItemRef.CUT &&
      item.ref !== BeaconCuttingData.FlowItemRef.SECTION &&
      icons.length < num &&
      !refsCollected.includes(item.ref)
    ) {
      refsCollected.push(item.ref);
      const waitForType = getWaitForType(item);
      icons.push(
        <FlowItemIcon
          className="edit-action-icon"
          icon={getFlowItemIcon(item.ref, undefined, waitForType?.value)}
          isItemSelected={isSelected}
        />
      );
    }
  });

  return isEmpty(icons) ? [defaultIcon] : icons;
};

export { CuttingEditActions };
